import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiHealthTrackerService } from '../health-tracker.service';
import {
  StressDayResponse,
  StressRangeResponse,
  StressRequestParamsDay,
  StressRequestParamsRange
} from './stress.interface';

/**
 * @description Stress Service.
 * @exports
 * @class ApiHealthTrackerStressService
 */
@Injectable()
export class ApiHealthTrackerStressService {
  private readonly apiHealthTrackerService = inject(ApiHealthTrackerService);

  /**
   * Fetches stress data for a specific day.
   *
   * @param {StressRequestParamsDay} params - The parameters for the request.
   * @returns {Observable<StressDayResponse>} An observable containing the stress data for the specified day.
   */
  fetchDayData(params: StressRequestParamsDay): Observable<StressDayResponse> {
    return this.apiHealthTrackerService
      .get<StressDayResponse>('/stress/day', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }
  /**
   * Fetches stress data for a specific month.
   *
   * @param {StressRequestParamsRange} params - The parameters for the request including start and end date range.
   * @returns {Observable<StressRangeResponse>} An observable containing the stress data for the specified month.
   */
  fetchMonthData(
    params: StressRequestParamsRange
  ): Observable<StressRangeResponse> {
    return this.apiHealthTrackerService
      .get<StressRangeResponse>('/stress/month', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }

  /**
   * Fetches stress data for a specific week.
   *
   * @param {StressRequestParamsRange} params - The parameters for the request including start and end date range.
   * @returns {Observable<StressRangeResponse>} An observable containing the stress data for the specified week.
   */
  fetchWeekData(
    params: StressRequestParamsRange
  ): Observable<StressRangeResponse> {
    return this.apiHealthTrackerService
      .get<StressRangeResponse>('/stress/week', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }

  /**
   * Fetches stress data for a specific year.
   *
   * @param {StressRequestParamsRange} params - The parameters for the request including start and end date range.
   * @returns {Observable<StressRangeResponse>} An observable containing the stress data for the specified year.
   */
  fetchYearData(
    params: StressRequestParamsRange
  ): Observable<StressRangeResponse> {
    return this.apiHealthTrackerService
      .get<StressRangeResponse>('/stress/year', {
        params: {
          ...params
        }
      })
      .pipe(
        map((response) => ({
          ...response,
          data:
            response.data && !Array.isArray(response.data)
              ? response.data
              : null
        }))
      );
  }
}
